import { createContext, useContext } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAppEnvironmentStore } from '../../../stores/AppEnvironment/appEnvironment.store';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useNetworkContext } from '../../offline/contexts/network/network.context';
import { UsedInOfflineMode } from '../../offline/types/UsedInOfflineMode';
import { useAbility } from '../../users/AbilityContext';
import { featureVisibilityRules } from '../rules/featureVisibility.rules';
import { FeatureVisibility, VisibilityContext } from '../types/featureVisibility.types';
import { FeatureKey } from '../types/features.keys';

const FeatureVisibilityContext = createContext<FeatureVisibility | null>(null);

interface FeatureVisibilityProviderProps extends UsedInOfflineMode {
  children: React.ReactNode;
}

export const FeatureVisibilityProvider = ({
  children,
  isOfflineMode = false,
}: FeatureVisibilityProviderProps) => {
  const ability = useAbility();
  const { isOnline } = useNetworkContext();
  const featureFlags = useFeatureFlag(isOfflineMode);
  const { isAdminBrand, isBrand, isSeller, isStore, integrations } = useApplicationContext();
  const appEnvironment = useAppEnvironmentStore((state) => state.appEnvironment);

  const visibilityContext: VisibilityContext = {
    ability,
    appEnvironment,
    isOfflineMode,
    isNetworkOffline: !isOnline,
    featureFlags,
    appContext: {
      isAdminBrand,
      isBrand,
      isSeller,
      isStore,
      integrations,
    },
  };

  const isVisible = (feature: FeatureKey): boolean => {
    const rule = featureVisibilityRules[feature];

    if (!rule) {
      throw new Error(`No visibility rule defined for feature: ${feature}`);
    }

    return rule(visibilityContext);
  };

  const value: FeatureVisibility = {
    isVisible,
    isHidden: (feature: FeatureKey) => !isVisible(feature),
  };

  return (
    <FeatureVisibilityContext.Provider value={value}>{children}</FeatureVisibilityContext.Provider>
  );
};

export const useFeatureVisibility = (): FeatureVisibility => {
  const context = useContext(FeatureVisibilityContext);

  if (!context) {
    throw new Error('useFeatureVisibility must be used within FeatureVisibilityProvider');
  }

  return context;
};
